import { useState, useEffect, useCallback, useMemo } from 'react';

import { oneTrustApi } from '../services/oneTrustApi';
import { CookieCategory, GroupedCookieCategory } from '../types/cookieTypes';

export interface UseCookieDataReturn {
  cookieCategories: CookieCategory[];
  nonEmptyCategories: CookieCategory[];
  groupedNonEmptyCookieCategories: GroupedCookieCategory[];
  isLoading: boolean;
  error: string | null;
}

export const useCookieData = (): UseCookieDataReturn => {
  const [cookieCategories, setCookieCategories] = useState<CookieCategory[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const categories = await oneTrustApi.fetchCookieData();
      const sortedCategories = categories.sort((a, b) =>
        a.optanonId.localeCompare(b.optanonId)
      );
      setCookieCategories(sortedCategories);
    } catch (err) {
      setError('Failed to load cookie information. Please try again later.');
      setCookieCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const nonEmptyCategories = useMemo(() => {
    return cookieCategories.filter((category) => category.cookies.length > 0);
  }, [cookieCategories]);

  const groupedNonEmptyCookieCategories =
    useMemo((): GroupedCookieCategory[] => {
      const parentCategories: CookieCategory[] = [];
      const childCategories: CookieCategory[] = [];

      nonEmptyCategories.forEach((category) => {
        if (category.optanonParentId) {
          childCategories.push(category);
        } else {
          parentCategories.push(category);
        }
      });

      const grouped: GroupedCookieCategory[] = parentCategories.map(
        (parent) => {
          const children = childCategories.filter(
            (child) => child.optanonParentId === parent.optanonId
          );
          return {
            ...parent,
            children,
          };
        }
      );

      const orphanedChildren = childCategories.filter(
        (child) =>
          !parentCategories.some(
            (parent) => parent.optanonId === child.optanonParentId
          )
      );

      orphanedChildren.forEach((child) => {
        grouped.push({
          ...child,
          children: [],
        });
      });

      return grouped;
    }, [nonEmptyCategories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    cookieCategories,
    groupedNonEmptyCookieCategories,
    nonEmptyCategories,
    isLoading,
    error,
  };
};
